/*
  assets/combo-sticker-builder.js
  Behaviour for snippets/combo-sticker-builder.liquid (Cool Kids Sticker Co).

  Customer picks one "(Top)" variant + one "(Bottom)" variant from the same
  product. We look up a merged preview image from the combo image map, update
  the summary / price / button, and add BOTH variants as two line items in a
  single /cart/add.js request.

  Expects the markup rendered by snippets/combo-sticker-builder.liquid.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-combo-builder]';
  var instances = new WeakMap();

  /* ------------------------------------------------------------------ utils */

  function show(el, on) {
    if (!el) return;
    el.hidden = !on;
    el.style.display = on ? '' : 'none';
  }

  function text(el, value) {
    if (el) el.textContent = value == null ? '' : value;
  }

  function toCents(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? 0 : n;
  }

  // Money formatting: prefer the theme's own formatter, fall back to the
  // shop money format string, then to Intl.
  function formatMoney(cents, format) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      try { return theme.Currency.formatMoney(cents, format); } catch (e) {}
    }
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try { return Shopify.formatMoney(cents, format); } catch (e) {}
    }
    return fallbackMoney(cents, format);
  }

  function fallbackMoney(cents, format) {
    var value = (cents || 0) / 100;
    var fmt = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholder);
    if (!match) return '$' + value.toFixed(2);

    var amount;
    switch (match[1]) {
      case 'amount_no_decimals':
        amount = group(Math.round(value).toString(), ',', '');
        break;
      case 'amount_with_comma_separator':
        amount = group(value.toFixed(2), '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = group(Math.round(value).toString(), '.', '');
        break;
      case 'amount_with_apostrophe_separator':
        amount = group(value.toFixed(2), "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = group(Math.round(value).toString(), ' ', '');
        break;
      case 'amount_with_space_separator':
        amount = group(value.toFixed(2), ' ', ',');
        break;
      default:
        amount = group(value.toFixed(2), ',', '.');
    }
    return fmt.replace(placeholder, amount);
  }

  // 1234.5 -> "1,234.50" with configurable thousands / decimal separators
  function group(numberString, thousands, decimal) {
    var parts = numberString.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    if (parts.length < 2 || !decimal) return whole;
    return whole + decimal + parts[1];
  }

  function moneyNoZeros(cents, format) {
    return formatMoney(cents, format).replace(/[.,]00(?=\D|$)/, '');
  }

  /* ------------------------------------------------------------- the builder */

  function ComboBuilder(root) {
    this.root = root;
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.buttonText = root.dataset.buttonText || 'Add to cart';
    this.productTitle = root.dataset.comboProductTitle || '';
    this.showFrom = root.dataset.showFrom === 'true';
    // Cart line item wording (set in the theme editor on the combo block).
    // One visible property per line item, e.g. "Top tub colour: Blush".
    this.cartProperty = {
      top: root.dataset.topCartProperty || 'Top tub colour',
      bottom: root.dataset.bottomCartProperty || 'Bottom tub colour'
    };
    // Off when the product's own option values already say top/bottom clearly
    this.addCartProperty = root.dataset.addCartProperty !== 'false';
    this.fromPrice = root.dataset.fromPrice ? toCents(root.dataset.fromPrice) : null;
    this.cartRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    this.el = {
      grids: root.querySelectorAll('[data-combo-grid]'),
      stepValue: {
        top: root.querySelector('[data-combo-step-value="top"]'),
        bottom: root.querySelector('[data-combo-step-value="bottom"]')
      },
      summaryWrap: root.querySelector('[data-combo-summary-wrap]'),
      summary: root.querySelector('[data-combo-summary]'),
      previewBtn: root.querySelector('[data-combo-preview-open]'),
      previewImg: root.querySelector('[data-combo-preview-img]'),
      priceBlock: root.querySelector('[data-combo-price]'),
      error: root.querySelector('[data-combo-error]'),
      button: root.querySelector('[data-combo-add]'),
      btnText: root.querySelector('[data-combo-btn-text]'),
      sep: root.querySelector('[data-combo-sep]'),
      priceInline: root.querySelector('[data-combo-price-inline]'),
      compare: root.querySelector('[data-combo-compare]'),
      badge: root.querySelector('[data-combo-badge]'),
      lightbox: root.querySelector('[data-combo-lightbox]'),
      lightboxImg: root.querySelector('[data-combo-lightbox-img]'),
      lightboxCaption: root.querySelector('[data-combo-lightbox-caption]')
    };

    // Prompts shown on the button before a choice is made — read from the
    // step legends so they always match the theme editor settings.
    this.prompts = {
      top: this.legendText('top') || 'Choose your top sticker',
      bottom: this.legendText('bottom') || 'Choose your bottom sticker'
    };

    this.imageMap = this.parseImageMap();
    this.selection = { top: null, bottom: null };
    this.originalMedia = null;
    this.busy = false;

    this.bind();
    if (root.dataset.hideNativeBuy === 'true') this.hideNativeBuy();

    // Honour any pre-checked input (theme editor re-renders, browser restore)
    var checked = root.querySelectorAll('[data-combo-input]:checked');
    for (var i = 0; i < checked.length; i++) this.select(checked[i]);
    this.render();
  }

  ComboBuilder.prototype.legendText = function (role) {
    var step = this.root.querySelector('[data-combo-step="' + role + '"] .radio__legend__label');
    return step ? step.textContent.trim() : '';
  };

  ComboBuilder.prototype.parseImageMap = function () {
    var node = this.root.querySelector('[data-combo-image-map]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent.trim() || '{}');
    } catch (e) {
      console.warn('[combo-builder] combo image map is not valid JSON', e);
      return {};
    }
  };

  ComboBuilder.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('change', function (evt) {
      var input = evt.target.closest ? evt.target.closest('[data-combo-input]') : null;
      if (!input) return;
      self.select(input);
      self.clearError();
      self.render();
    });

    if (this.el.button) {
      this.el.button.addEventListener('click', function (evt) {
        evt.preventDefault();
        self.addToCart();
      });
    }

    if (this.el.previewBtn) {
      this.el.previewBtn.addEventListener('click', function () { self.openLightbox(); });
    }

    var closers = this.root.querySelectorAll('[data-combo-lightbox-close]');
    for (var i = 0; i < closers.length; i++) {
      closers[i].addEventListener('click', function () { self.closeLightbox(); });
    }

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape') self.closeLightbox();
    });
  };

  ComboBuilder.prototype.select = function (input) {
    var tile = input.closest('[data-combo-option]');
    if (!tile) return;
    var role = tile.dataset.role;
    if (role !== 'top' && role !== 'bottom') return;

    this.selection[role] = {
      id: tile.dataset.variantId,
      price: toCents(tile.dataset.price),
      compare: toCents(tile.dataset.compare),
      name: tile.dataset.name || tile.dataset.title || '',
      color: tile.dataset.color || tile.dataset.name || tile.dataset.title || '',
      title: tile.dataset.title || ''
    };
  };

  ComboBuilder.prototype.comboKey = function () {
    var t = this.selection.top;
    var b = this.selection.bottom;
    return t && b ? t.id + '|' + b.id : null;
  };

  ComboBuilder.prototype.comboLabel = function () {
    var t = this.selection.top;
    var b = this.selection.bottom;
    if (!t || !b) return '';
    return t.name + ' + ' + b.name;
  };

  /* ------------------------------------------------------------------ render */

  ComboBuilder.prototype.render = function () {
    var t = this.selection.top;
    var b = this.selection.bottom;
    var both = !!(t && b);

    text(this.el.stepValue.top, t ? t.name : '');
    text(this.el.stepValue.bottom, b ? b.name : '');

    // Summary + preview
    show(this.el.summaryWrap, both);
    text(this.el.summary, this.comboLabel());
    this.renderPreview();

    // Price
    var total = (t ? t.price : 0) + (b ? b.price : 0);
    var compareTotal = (t ? (t.compare || t.price) : 0) + (b ? (b.compare || b.price) : 0);
    var saving = compareTotal - total;

    if (both) {
      text(this.el.btnText, this.buttonText);
      show(this.el.sep, true);
      show(this.el.priceInline, true);
      text(this.el.priceInline, formatMoney(total, this.moneyFormat));

      show(this.el.compare, saving > 0);
      if (saving > 0) text(this.el.compare, formatMoney(compareTotal, this.moneyFormat));

      show(this.el.badge, saving > 0);
      if (saving > 0) text(this.el.badge, 'Save ' + moneyNoZeros(saving, this.moneyFormat));

      show(this.el.priceBlock, false);
      this.el.button.disabled = false;
    } else {
      text(this.el.btnText, t ? this.prompts.bottom : this.prompts.top);
      show(this.el.sep, false);
      show(this.el.priceInline, false);
      show(this.el.compare, false);
      show(this.el.badge, false);

      var showFromPrice = this.showFrom && this.fromPrice != null;
      show(this.el.priceBlock, showFromPrice);
      if (showFromPrice) {
        text(this.el.priceBlock, 'From ' + formatMoney(this.fromPrice, this.moneyFormat));
      }
      this.el.button.disabled = true;
    }
  };

  ComboBuilder.prototype.renderPreview = function () {
    var key = this.comboKey();
    var entry = key ? this.imageMap[key] : null;

    if (!entry || !entry.image) {
      show(this.el.previewBtn, false);
      this.restoreGallery();
      return;
    }

    var alt = entry.alt || this.comboLabel();
    if (this.el.previewImg) {
      this.el.previewImg.src = entry.image;
      this.el.previewImg.alt = alt;
    }
    if (this.el.lightboxImg) {
      this.el.lightboxImg.src = entry.image;
      this.el.lightboxImg.alt = alt;
    }
    text(this.el.lightboxCaption, this.comboLabel());
    show(this.el.previewBtn, true);
    this.swapGallery(entry.image, alt);
  };

  /* --------------------------------------------------------- gallery preview */

  ComboBuilder.prototype.galleryImg = function () {
    var slideshow = document.querySelector('[data-product-slideshow]');
    return slideshow ? slideshow.querySelector('img') : null;
  };

  ComboBuilder.prototype.swapGallery = function (src, alt) {
    try {
      var img = this.galleryImg();
      if (!img) return;
      if (!this.originalMedia) {
        this.originalMedia = {
          el: img,
          src: img.getAttribute('src'),
          srcset: img.getAttribute('srcset'),
          sizes: img.getAttribute('sizes'),
          alt: img.getAttribute('alt')
        };
      }
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = src;
      img.alt = alt || '';

      // Make sure the first slide is the one on screen.
      var slideshow = document.querySelector('[data-product-slideshow]');
      if (slideshow && window.Flickity && typeof Flickity.data === 'function') {
        var flkty = Flickity.data(slideshow);
        if (flkty && typeof flkty.select === 'function') flkty.select(0);
      }
    } catch (e) {
      /* gallery swap is a nice-to-have — never let it break the builder */
    }
  };

  ComboBuilder.prototype.restoreGallery = function () {
    var o = this.originalMedia;
    if (!o || !o.el) return;
    try {
      if (o.src) o.el.setAttribute('src', o.src); else o.el.removeAttribute('src');
      if (o.srcset) o.el.setAttribute('srcset', o.srcset);
      if (o.sizes) o.el.setAttribute('sizes', o.sizes);
      if (o.alt != null) o.el.setAttribute('alt', o.alt);
    } catch (e) {}
    this.originalMedia = null;
  };

  /* ---------------------------------------------------------------- lightbox */

  ComboBuilder.prototype.openLightbox = function () {
    if (!this.el.lightbox || !this.el.lightboxImg || !this.el.lightboxImg.src) return;
    show(this.el.lightbox, true);
    this.el.lightbox.classList.add('is-open');
    document.documentElement.classList.add('cksc-combo-lightbox-open');
  };

  ComboBuilder.prototype.closeLightbox = function () {
    if (!this.el.lightbox) return;
    show(this.el.lightbox, false);
    this.el.lightbox.classList.remove('is-open');
    document.documentElement.classList.remove('cksc-combo-lightbox-open');
  };

  /* ------------------------------------------------------------- add to cart */

  ComboBuilder.prototype.setBusy = function (busy) {
    this.busy = busy;
    if (!this.el.button) return;
    this.el.button.classList.toggle('loading', busy);
    this.el.button.disabled = busy;
  };

  ComboBuilder.prototype.showError = function (message) {
    if (!this.el.error) return;
    text(this.el.error, message);
    show(this.el.error, true);
  };

  ComboBuilder.prototype.clearError = function () {
    show(this.el.error, false);
    text(this.el.error, '');
  };

  ComboBuilder.prototype.addToCart = function () {
    var self = this;
    var t = this.selection.top;
    var b = this.selection.bottom;
    if (!t || !b || this.busy) return;

    var comboId = t.id + '-' + b.id;

    // No combined "Combo: top + bottom" property — the product's own option
    // values already say which half each line item is. Only _combo_id goes
    // through: underscore-prefixed, so Shopify keeps it on the order (to pair
    // the two halves for picking) but never shows it in the cart or checkout.
    // The colour property below is optional and off by default.
    var properties = function (role, part) {
      var props = {};
      if (self.addCartProperty) props[self.cartProperty[role]] = part.color;
      props._combo_id = comboId;
      return props;
    };

    var payload = {
      items: [
        { id: Number(t.id), quantity: 1, properties: properties('top', t) },
        { id: Number(b.id), quantity: 1, properties: properties('bottom', b) }
      ]
    };

    this.clearError();
    this.setBusy(true);

    fetch(this.cartRoot + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Add to cart failed');
          return data;
        });
      })
      .then(function () {
        self.setBusy(false);
        self.el.button.classList.add('has-success');
        self.openCart();
        setTimeout(function () {
          self.el.button.classList.remove('has-success');
        }, 1200);
      })
      .catch(function (err) {
        self.setBusy(false);
        self.showError(err && err.message ? err.message : 'Sorry, we could not add this combo to your cart.');
        console.error('[combo-builder]', err);
      });
  };

  ComboBuilder.prototype.openCart = function () {
    // Ask the theme to refresh its drawer, then open it.
    document.dispatchEvent(new CustomEvent('theme:cart:reload'));
    var toggle = document.querySelector('[data-drawer-toggle="drawer-cart"]');
    if (toggle) {
      setTimeout(function () { toggle.click(); }, 100);
    } else {
      window.location.href = this.cartRoot + 'cart';
    }
  };

  /* --------------------------------------------------- optional native hiding */

  ComboBuilder.prototype.hideNativeBuy = function () {
    var sectionId = this.root.dataset.sectionId;
    var scope = (sectionId && document.getElementById('shopify-section-' + sectionId)) || document;
    var targets = scope.querySelectorAll(
      'form[data-product-form], .product__block__variants, [data-product-option], .product__submit__holder'
    );
    for (var i = 0; i < targets.length; i++) {
      if (this.root.contains(targets[i])) continue;
      targets[i].style.display = 'none';
    }
  };

  /* -------------------------------------------------------------------- init */

  function init(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      if (instances.has(roots[i])) continue;
      try {
        instances.set(roots[i], new ComboBuilder(roots[i]));
      } catch (e) {
        console.error('[combo-builder] failed to initialise', e);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (evt) { init(evt.target); });
  document.addEventListener('shopify:block:select', function (evt) { init(evt.target); });

  window.ComboStickerBuilder = { init: init };
})();